import React, { useEffect, useState } from 'react';
import {
  Settings, Save, Upload, Home, FileText, Phone,
  DollarSign, Mail, Globe, MapPin, CheckCircle, AlertCircle,
  Loader, Image as ImageIcon, Shield, Megaphone, ArrowLeftRight, Package
} from 'lucide-react';
import { getConfigurations, updateConfiguration, initConfigurations, uploadImage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { useBoutique } from '../context/BoutiqueContext';
import { PRODUITS_COLONNES, PRODUITS_COLONNES_DEFAUT } from '../constants/produitsColonnes';
import './Configuration.css';

const DEFAULTS = {
  taux_tva: 18, tva_par_default: true, devise: 'XOF',
  nom_entreprise: 'Mon Entreprise', ninea: '', rc: '',
  telephone: '', email: '', site_web: '', adresse: '', position: '', logo: '',
  mode_vente_defaut: 'comptant',
  autoriser_vente_sans_stock: false,
  taux_change: { EUR: 655.957, USD: 600, GNF: 0.071, MAD: 65 },
  modules_enabled_marketing: true,
  modules_enabled_ecommerce: true,
  modules_enabled_loyalty: true,
  facture_champs: { coordonnees: true, mentions: true, clientContact: true, vendeur: true },
  facture_pied_page: '',
  transfert_direct: false,
  transfert_types_autorises: ['entrepot-entrepot', 'entrepot-boutique', 'boutique-entrepot', 'boutique-boutique'],
  transfert_type_defaut: 'entrepot-boutique',
  boutique_defaut: 'all',
  produits_colonnes_visibles: PRODUITS_COLONNES_DEFAUT
};

const TRANSFERT_TYPES = [
  ['entrepot-entrepot', 'Entrepôt → Entrepôt'],
  ['entrepot-boutique', 'Entrepôt → Boutique'],
  ['boutique-entrepot', 'Boutique → Entrepôt'],
  ['boutique-boutique', 'Boutique → Boutique'],
];

const CONFIG_KEYS = ['taux_tva','tva_par_default','devise','nom_entreprise','ninea','rc','telephone','email','site_web','adresse','position','logo','mode_vente_defaut','autoriser_vente_sans_stock','taux_change','modules_enabled_marketing','modules_enabled_ecommerce','modules_enabled_loyalty','facture_champs','facture_pied_page','transfert_direct','transfert_types_autorises','transfert_type_defaut','boutique_defaut','produits_colonnes_visibles'];
const DESCRIPTIONS = {
  taux_tva:'Taux de TVA par défaut (%)', tva_par_default:'Appliquer la TVA par défaut sur les ventes',
  devise:'Devise par défaut', nom_entreprise:"Nom de l'entreprise", ninea:'Numéro NINEA',
  rc:'Registre de Commerce', telephone:"Téléphone de l'entreprise", email:"Email de contact de l'entreprise",
  site_web:"Site web de l'entreprise", adresse:"Adresse complète de l'entreprise",
  position:'Position/Localisation', logo:"URL du logo de l'entreprise",
  mode_vente_defaut: 'Mode de vente par défaut sur la page Nouvelle vente',
  autoriser_vente_sans_stock: 'Autoriser la vente sans stock (stock négatif, réajusté à la réception)',
  modules_enabled_marketing: 'Activer/désactiver le module Marketing (CRM, Creative, Publishing, Attribution)',
  modules_enabled_ecommerce: 'Activer/désactiver le module E-commerce (Vitrines, Commandes en ligne, Import stock)',
  modules_enabled_loyalty: 'Activer/désactiver le module Fidélité (cartes, points, récompenses)',
  facture_champs: 'Champs affichés sur la facture / ticket',
  facture_pied_page: 'Texte de pied de page de la facture / ticket',
  transfert_direct: 'Transfert direct : le stock est déplacé dès la création (sans valider / expédier / recevoir)',
  transfert_types_autorises: "Types de transfert autorisés pour l'entreprise",
  transfert_type_defaut: 'Type de transfert présélectionné dans Nouveau transfert',
  boutique_defaut: 'Boutique sélectionnée par défaut à la connexion (admins multi-boutiques)',
  produits_colonnes_visibles: 'Colonnes visibles par défaut dans le tableau des produits'
};

export default function Configuration() {
  const { user }                          = useAuth();
  const { reloadConfig }                  = useConfig();
  const { boutiques }                     = useBoutique();
  const [config,     setConfig]           = useState(DEFAULTS);
  const [loading,    setLoading]          = useState(true);
  const [saving,     setSaving]           = useState(false);
  const [uploading,  setUploading]        = useState(false);
  const [toast,      setToast]            = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    try {
      const { data } = await getConfigurations();
      if (data.data && Object.keys(data.data).length > 0) {
        setConfig({ ...DEFAULTS, ...data.data });
      } else {
        await initConfigurations();
        const { data: nd } = await getConfigurations();
        setConfig({ ...DEFAULTS, ...nd.data });
      }
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const set = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('error', 'Veuillez sélectionner une image'); return; }
    if (file.size > 5 * 1024 * 1024)   { showToast('error', "L'image ne doit pas dépasser 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await uploadImage(fd);
      const url = data.data.url;
      setConfig(c => ({ ...c, logo: url }));
      await updateConfiguration('logo', { valeur: url, description: DESCRIPTIONS.logo });
      showToast('success', 'Logo uploadé avec succès');
    } catch (err) { showToast('error', err.response?.data?.message || "Erreur lors de l'upload"); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!typesTransfert.length) { showToast('error', 'Autorisez au moins un type de transfert'); return; }
    setSaving(true);
    try {
      for (const key of CONFIG_KEYS) {
        let valeur = config[key];
        if (key === 'taux_tva') valeur = parseFloat(valeur);
        await updateConfiguration(key, { valeur, description: DESCRIPTIONS[key] });
      }
      await reloadConfig();
      showToast('success', 'Configuration sauvegardée avec succès');
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="cfg-loading"><Loader className="cfg-spinner" size={26}/><span>Chargement…</span></div>
  );

  const tvaChecked = config.tva_par_default === true || config.tva_par_default === 'true';
  const marketingChecked = config.modules_enabled_marketing === true || config.modules_enabled_marketing === 'true';
  const ecommerceChecked = config.modules_enabled_ecommerce === true || config.modules_enabled_ecommerce === 'true';
  const loyaltyChecked = config.modules_enabled_loyalty === true || config.modules_enabled_loyalty === 'true';
  const directChecked = config.transfert_direct === true || config.transfert_direct === 'true';
  const typesTransfert = Array.isArray(config.transfert_types_autorises) ? config.transfert_types_autorises : DEFAULTS.transfert_types_autorises;
  // Coche/décoche un type ; le type par défaut reste toujours parmi les types autorisés
  const toggleTypeTransfert = (t) => {
    const next = typesTransfert.includes(t)
      ? typesTransfert.filter(x => x !== t)
      : TRANSFERT_TYPES.map(([k]) => k).filter(k => k === t || typesTransfert.includes(k));
    setConfig(c => ({
      ...c,
      transfert_types_autorises: next,
      transfert_type_defaut: next.includes(c.transfert_type_defaut) ? c.transfert_type_defaut : (next[0] || ''),
    }));
  };
  const colonnesProduits = Array.isArray(config.produits_colonnes_visibles) ? config.produits_colonnes_visibles : PRODUITS_COLONNES_DEFAUT;
  const toggleColonneProduit = (k) => set('produits_colonnes_visibles',
    colonnesProduits.includes(k) ? colonnesProduits.filter(x => x !== k) : [...colonnesProduits, k]);
  const fc = { coordonnees: true, mentions: true, clientContact: true, vendeur: true, detailNom: true, detailCode: true, detailPrixUnitaire: true, detailQuantite: true, detailCodeBarres: false, ...(config.facture_champs || {}) };
  const setFc = (k) => set('facture_champs', { ...fc, [k]: !fc[k] });
  const FACTURE_FIELDS = [
    ['coordonnees',   'Logo & coordonnées entreprise', 'Adresse, téléphone, email, site web'],
    ['mentions',      'Mentions légales',              'NINEA et Registre de Commerce (RC)'],
    ['clientContact', 'Contact client',                'Téléphone et email du client'],
    ['vendeur',       'Vendeur / caissier',            'Nom du vendeur (et caissier sur le ticket)'],
  ];
  const DETAIL_FIELDS = [
    ['detailNom',          'Nom du produit', 'Affiche le nom du produit dans le tableau'],
    ['detailCode',         'Code produit',  'Affiche le code produit sous chaque article'],
    ['detailCodeBarres',   'Code-barres',   'Affiche le code-barres sous chaque article'],
    ['detailQuantite',     'Quantité',      'Affiche la colonne Quantité'],
    ['detailPrixUnitaire', 'Prix unitaire', 'Affiche la colonne prix unitaire du tableau'],
  ];

  return (
    <div className="cfg-page">

      {/* Toast */}
      {toast && (
        <div className={`cfg-toast cfg-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="cfg-header">
        <div className="cfg-header-left">
          <div className="cfg-header-icon"><Settings size={18}/></div>
          <div>
            <h1 className="cfg-header-title">Configuration</h1>
            <p className="cfg-header-sub">Informations et paramètres de votre entreprise</p>
          </div>
        </div>
        {user && (
          <div className="cfg-role-badge">
            <Shield size={12}/>
            {user.role === 'admin-entreprise' ? 'Admin Entreprise' : user.role}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cfg-content">

          {/* ── Section: Général ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <Home size={14}/> Informations générales
            </div>
            <div className="cfg-section-body">

              <div className="cfg-field">
                <label>Nom de l'entreprise *</label>
                <input type="text" required placeholder="Entrez le nom de votre entreprise"
                  value={config.nom_entreprise}
                  onChange={e => set('nom_entreprise', e.target.value)}/>
              </div>

              {/* Logo */}
              <div className="cfg-field">
                <label>Logo de l'entreprise</label>
                <div className="cfg-logo-zone">
                  {config.logo ? (
                    <div className="cfg-logo-preview">
                      <img src={config.logo} alt="Logo"
                        onError={e => { e.target.src = ''; e.target.style.display='none'; }}/>
                    </div>
                  ) : (
                    <div className="cfg-logo-placeholder">
                      <ImageIcon size={28} color="#94a3b8"/>
                      <span>Aucun logo</span>
                    </div>
                  )}
                  <div className="cfg-logo-actions">
                    <input type="file" accept="image/*" id="cfg-logo-input"
                      className="cfg-file-hidden" onChange={handleLogoUpload}/>
                    <label htmlFor="cfg-logo-input"
                      className={`cfg-upload-btn ${uploading ? 'cfg-upload-btn--busy' : ''}`}>
                      {uploading
                        ? <><Loader size={13} className="cfg-spin"/> Upload en cours…</>
                        : <><Upload size={13}/> {config.logo ? 'Changer le logo' : 'Ajouter un logo'}</>}
                    </label>
                    <span className="cfg-upload-hint">JPG, PNG, WEBP — max 5 MB</span>
                  </div>
                </div>
              </div>

              {/* Boutique par défaut */}
              <div className="cfg-field">
                <label>Boutique par défaut</label>
                <select value={config.boutique_defaut || 'all'}
                  onChange={e => set('boutique_defaut', e.target.value)}>
                  <option value="all">Toutes les boutiques</option>
                  {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                </select>
                <p className="cfg-toggle-hint" style={{ marginTop: 4 }}>
                  Boutique sélectionnée automatiquement à la connexion pour les administrateurs.
                  Les admins boutique, gestionnaires et caissiers restent sur leur propre boutique.
                </p>
              </div>
            </div>
          </div>

          {/* ── Section: Légal ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <FileText size={14}/> Informations légales
            </div>
            <div className="cfg-section-body">
              <div className="cfg-row2">
                <div className="cfg-field">
                  <label>NINEA</label>
                  <input type="text" placeholder="ex: 000000000"
                    value={config.ninea} onChange={e => set('ninea', e.target.value)}/>
                </div>
                <div className="cfg-field">
                  <label>Registre de Commerce (RC)</label>
                  <input type="text" placeholder="ex: SN-DKR-2024-A-00000"
                    value={config.rc} onChange={e => set('rc', e.target.value)}/>
                </div>
              </div>
            </div>
          </div>

          {/* ── Section: Contact ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <Phone size={14}/> Informations de contact
            </div>
            <div className="cfg-section-body">
              <div className="cfg-row2">
                <div className="cfg-field">
                  <label><Phone size={11}/> Téléphone</label>
                  <input type="tel" placeholder="ex: +221 77 123 45 67"
                    value={config.telephone} onChange={e => set('telephone', e.target.value)}/>
                </div>
                <div className="cfg-field">
                  <label><Mail size={11}/> Email de contact</label>
                  <input type="email" placeholder="ex: contact@monentreprise.sn"
                    value={config.email} onChange={e => set('email', e.target.value)}/>
                </div>
              </div>

              <div className="cfg-field">
                <label><Globe size={11}/> Site web</label>
                <input type="url" placeholder="ex: https://www.monentreprise.sn"
                  value={config.site_web} onChange={e => set('site_web', e.target.value)}/>
              </div>

              <div className="cfg-field">
                <label><MapPin size={11}/> Adresse complète</label>
                <textarea rows={3} placeholder="Rue, quartier, ville, pays"
                  value={config.adresse} onChange={e => set('adresse', e.target.value)}/>
              </div>

              <div className="cfg-field">
                <label><MapPin size={11}/> Position / Localisation</label>
                <input type="text" placeholder="Coordonnées GPS ou point de repère"
                  value={config.position} onChange={e => set('position', e.target.value)}/>
              </div>
            </div>
          </div>

          {/* ── Section: Finance ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <DollarSign size={14}/> Paramètres financiers
            </div>
            <div className="cfg-section-body">
              <div className="cfg-row2">
                <div className="cfg-field">
                  <label>Devise *</label>
                  <select required value={config.devise} onChange={e => set('devise', e.target.value)}>
                    <option value="XOF">XOF — Franc</option>
                    <option value="EUR">EUR — Euro</option>
                    <option value="USD">USD — Dollar</option>
                    <option value="GNF">GNF — Franc guinéen</option>
                    <option value="MAD">MAD — Dirham marocain</option>
                  </select>
                </div>
                <div className="cfg-field">
                  <label>Taux de TVA par défaut (%) *</label>
                  <input type="number" required min="0" max="100" step="0.1"
                    value={config.taux_tva} onChange={e => set('taux_tva', e.target.value)}/>
                </div>
              </div>

              <div className="cfg-toggle-row">
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${tvaChecked ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('tva_par_default', !tvaChecked)}
                    role="checkbox" aria-checked={tvaChecked} tabIndex={0}
                    onKeyDown={e => e.key === ' ' && set('tva_par_default', !tvaChecked)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Appliquer la TVA par défaut sur les nouvelles ventes</span>
                </label>
                <p className="cfg-toggle-hint">
                  Si activé, la TVA sera cochée par défaut lors de la création d'une vente. Le caissier pourra toujours la décocher.
                </p>
              </div>

              {/* Mode de vente par défaut (comptant / crédit) */}
              <div className="cfg-field" style={{ marginTop: 16 }}>
                <label>Mode de vente par défaut</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button"
                    onClick={() => set('mode_vente_defaut', 'comptant')}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: config.mode_vente_defaut === 'comptant' ? '1.5px solid #16a34a' : '1px solid #e2e8f0',
                      background: config.mode_vente_defaut === 'comptant' ? '#dcfce7' : '#fff',
                      color: config.mode_vente_defaut === 'comptant' ? '#15803d' : '#374151',
                      fontWeight: config.mode_vente_defaut === 'comptant' ? 700 : 500,
                      fontSize: 13,
                    }}>
                    Comptant
                  </button>
                  <button type="button"
                    onClick={() => set('mode_vente_defaut', 'credit')}
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                      border: config.mode_vente_defaut === 'credit' ? '1.5px solid #d97706' : '1px solid #e2e8f0',
                      background: config.mode_vente_defaut === 'credit' ? '#fef3c7' : '#fff',
                      color: config.mode_vente_defaut === 'credit' ? '#92400e' : '#374151',
                      fontWeight: config.mode_vente_defaut === 'credit' ? 700 : 500,
                      fontSize: 13,
                    }}>
                    À crédit
                  </button>
                </div>
                <p className="cfg-toggle-hint" style={{ marginTop: 6 }}>
                  S'applique à l'ouverture de la page <em>Nouvelle vente</em>. Le caissier peut toujours basculer manuellement.
                </p>
              </div>

              {/* Vente sans stock (à découvert) */}
              <div className="cfg-toggle-row" style={{ marginTop: 16 }}>
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${config.autoriser_vente_sans_stock ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('autoriser_vente_sans_stock', !config.autoriser_vente_sans_stock)}
                    role="switch" tabIndex={0} aria-checked={!!config.autoriser_vente_sans_stock}
                    onKeyDown={e => e.key === ' ' && set('autoriser_vente_sans_stock', !config.autoriser_vente_sans_stock)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Autoriser la vente sans stock (à découvert)</span>
                </label>
                <p className="cfg-toggle-hint">
                  Si activé, la caisse peut vendre même quand le stock est insuffisant. Le stock passe en négatif et se réajuste automatiquement à la prochaine réception / réapprovisionnement.
                </p>
              </div>

              {/* Taux de change : 1 unité devise étrangère = X devise de base */}
              <div className="cfg-field" style={{ marginTop: 16 }}>
                <label>Taux de change</label>
                <p className="cfg-toggle-hint" style={{ marginTop: 0, marginBottom: 8 }}>
                  Combien d'unités de la devise de base ({config.devise || 'XOF'}) vaut 1 unité de chaque devise étrangère.
                  Utilisé pour convertir les montants à la volée sur la page Factures.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {Object.entries(config.taux_change || {}).map(([devise, rate]) => (
                    <div key={devise} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 8,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 36 }}>1 {devise}</span>
                      <span style={{ color: '#94a3b8' }}>=</span>
                      <input type="number" step="any" min="0"
                        value={rate}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          set('taux_change', { ...(config.taux_change || {}), [devise]: isNaN(v) ? 0 : v });
                        }}
                        style={{
                          flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0',
                          borderRadius: 6, fontSize: 13, fontWeight: 600,
                          color: '#0f172a', outline: 'none',
                          fontVariantNumeric: 'tabular-nums',
                        }}/>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{config.devise || 'XOF'}</span>
                      <button type="button"
                        onClick={() => {
                          const next = { ...(config.taux_change || {}) };
                          delete next[devise];
                          set('taux_change', next);
                        }}
                        style={{
                          width: 22, height: 22, border: 'none',
                          background: '#fee2e2', color: '#dc2626',
                          borderRadius: 5, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Supprimer ce taux">×</button>
                    </div>
                  ))}
                </div>
                <button type="button"
                  onClick={() => {
                    const code = prompt('Code ISO de la devise (ex: GBP, JPY, CHF) :');
                    if (!code) return;
                    const c = code.trim().toUpperCase();
                    if (config.taux_change?.[c]) { alert('Devise déjà présente'); return; }
                    set('taux_change', { ...(config.taux_change || {}), [c]: 1 });
                  }}
                  style={{
                    marginTop: 10, padding: '8px 14px',
                    background: '#fff', border: '1.5px solid #c7d2fe',
                    color: '#6366f1', borderRadius: 7,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}>
                  + Ajouter une devise
                </button>
              </div>
            </div>
          </div>

          {/* ── Section: Facture ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <FileText size={14}/> Champs de la facture
            </div>
            <div className="cfg-section-body">
              <p className="cfg-toggle-hint" style={{ marginTop: 0 }}>
                Choisissez les informations affichées sur la facture PDF, le ticket de caisse et l'aperçu à l'écran.
              </p>
              {FACTURE_FIELDS.map(([key, label, hint]) => (
                <div className="cfg-toggle-row" key={key}>
                  <label className="cfg-toggle-label">
                    <div className={`cfg-toggle ${fc[key] ? 'cfg-toggle--on' : ''}`}
                      onClick={() => setFc(key)}
                      role="checkbox" aria-checked={fc[key]} tabIndex={0}
                      onKeyDown={e => e.key === ' ' && setFc(key)}>
                      <div className="cfg-toggle-thumb"/>
                    </div>
                    <span className="cfg-toggle-text">{label}</span>
                  </label>
                  <p className="cfg-toggle-hint">{hint}</p>
                </div>
              ))}

              <div className="cfg-section-head" style={{ marginTop: 18, marginBottom: 4, fontSize: 13 }}>
                Détails de la vente (colonnes)
              </div>
              {DETAIL_FIELDS.map(([key, label, hint]) => (
                <div className="cfg-toggle-row" key={key}>
                  <label className="cfg-toggle-label">
                    <div className={`cfg-toggle ${fc[key] ? 'cfg-toggle--on' : ''}`}
                      onClick={() => setFc(key)}
                      role="checkbox" aria-checked={fc[key]} tabIndex={0}
                      onKeyDown={e => e.key === ' ' && setFc(key)}>
                      <div className="cfg-toggle-thumb"/>
                    </div>
                    <span className="cfg-toggle-text">{label}</span>
                  </label>
                  <p className="cfg-toggle-hint">{hint}</p>
                </div>
              ))}

              <div className="cfg-field" style={{ marginTop: 12 }}>
                <label>Texte de pied de page</label>
                <textarea rows={2} placeholder="Ex : Merci de votre visite — échange sous 7 jours avec ticket"
                  value={config.facture_pied_page || ''}
                  onChange={e => set('facture_pied_page', e.target.value)}/>
                <p className="cfg-toggle-hint" style={{ marginTop: 6 }}>
                  Affiché en bas de la facture et du ticket. Laissez vide pour le message par défaut.
                </p>
              </div>
            </div>
          </div>

          {/* ── Section: Produits ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <Package size={14}/> Produits — colonnes visibles
            </div>
            <div className="cfg-section-body">
              <p className="cfg-toggle-hint" style={{ marginTop: 0 }}>
                Colonnes affichées par défaut dans le tableau de la page <em>Produits</em>.
                Chacun peut encore les ajuster ponctuellement via le bouton « Colonnes ».
              </p>
              {PRODUITS_COLONNES.map(col => {
                const on = col.always || colonnesProduits.includes(col.key);
                return (
                  <div className="cfg-toggle-row" key={col.key}>
                    <label className="cfg-toggle-label" style={col.always ? { opacity: .6, cursor: 'default' } : undefined}>
                      <div className={`cfg-toggle ${on ? 'cfg-toggle--on' : ''}`}
                        onClick={() => !col.always && toggleColonneProduit(col.key)}
                        role="checkbox" aria-checked={on} aria-disabled={!!col.always} tabIndex={col.always ? -1 : 0}
                        onKeyDown={e => !col.always && e.key === ' ' && toggleColonneProduit(col.key)}>
                        <div className="cfg-toggle-thumb"/>
                      </div>
                      <span className="cfg-toggle-text">{col.label}{col.always ? ' (toujours affichée)' : ''}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Section: Transferts de stock ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <ArrowLeftRight size={14}/> Transferts de stock
            </div>
            <div className="cfg-section-body">
              <div className="cfg-toggle-row">
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${directChecked ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('transfert_direct', !directChecked)}
                    role="switch" aria-checked={directChecked} tabIndex={0}
                    onKeyDown={e => e.key === ' ' && set('transfert_direct', !directChecked)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Transfert direct</span>
                </label>
                <p className="cfg-toggle-hint">
                  Si activé, un <em>Nouveau transfert</em> déplace le stock immédiatement (statut « Reçue »),
                  sans les étapes Valider → Expédier → Recevoir. Le stock disponible à la source est toujours vérifié.
                </p>
              </div>

              <div className="cfg-section-head" style={{ marginTop: 18, marginBottom: 4, fontSize: 13 }}>
                Types de transfert autorisés
              </div>
              {TRANSFERT_TYPES.map(([key, label]) => {
                const on = typesTransfert.includes(key);
                return (
                  <div className="cfg-toggle-row" key={key}>
                    <label className="cfg-toggle-label">
                      <div className={`cfg-toggle ${on ? 'cfg-toggle--on' : ''}`}
                        onClick={() => toggleTypeTransfert(key)}
                        role="checkbox" aria-checked={on} tabIndex={0}
                        onKeyDown={e => e.key === ' ' && toggleTypeTransfert(key)}>
                        <div className="cfg-toggle-thumb"/>
                      </div>
                      <span className="cfg-toggle-text">{label}</span>
                    </label>
                  </div>
                );
              })}
              {!typesTransfert.length && (
                <p className="cfg-toggle-hint" style={{ color: '#dc2626' }}>Autorisez au moins un type de transfert.</p>
              )}

              <div className="cfg-field" style={{ marginTop: 16 }}>
                <label>Type de transfert par défaut</label>
                <select value={config.transfert_type_defaut || ''} disabled={!typesTransfert.length}
                  onChange={e => set('transfert_type_defaut', e.target.value)}>
                  {TRANSFERT_TYPES.filter(([k]) => typesTransfert.includes(k)).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
                <p className="cfg-toggle-hint" style={{ marginTop: 4 }}>
                  Présélectionné dans <em>Nouveau transfert</em> ; seuls les types autorisés y sont proposés.
                </p>
              </div>
            </div>
          </div>

          {/* ── Section: Modules ── */}
          <div className="cfg-section">
            <div className="cfg-section-head">
              <Megaphone size={14}/> Modules
            </div>
            <div className="cfg-section-body">
              <div className="cfg-toggle-row">
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${marketingChecked ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('modules_enabled_marketing', !marketingChecked)}
                    role="checkbox" aria-checked={marketingChecked} tabIndex={0}
                    onKeyDown={e => e.key === ' ' && set('modules_enabled_marketing', !marketingChecked)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Activer le module Marketing</span>
                </label>
                <p className="cfg-toggle-hint">
                  Affiche le menu <em>Marketing</em> (Funnel CRM, Créa Studio, Publications, Attribution).
                  Si désactivé, ce menu est masqué pour tous les utilisateurs.
                </p>
              </div>

              <div className="cfg-toggle-row">
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${ecommerceChecked ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('modules_enabled_ecommerce', !ecommerceChecked)}
                    role="checkbox" aria-checked={ecommerceChecked} tabIndex={0}
                    onKeyDown={e => e.key === ' ' && set('modules_enabled_ecommerce', !ecommerceChecked)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Activer le module E-commerce</span>
                </label>
                <p className="cfg-toggle-hint">
                  Affiche le menu <em>E-commerce</em> (Vitrines & Liens, Commandes en ligne, Import stock).
                  Si désactivé, ce menu est masqué pour tous les utilisateurs.
                </p>
              </div>

              <div className="cfg-toggle-row">
                <label className="cfg-toggle-label">
                  <div className={`cfg-toggle ${loyaltyChecked ? 'cfg-toggle--on' : ''}`}
                    onClick={() => set('modules_enabled_loyalty', !loyaltyChecked)}
                    role="checkbox" aria-checked={loyaltyChecked} tabIndex={0}
                    onKeyDown={e => e.key === ' ' && set('modules_enabled_loyalty', !loyaltyChecked)}>
                    <div className="cfg-toggle-thumb"/>
                  </div>
                  <span className="cfg-toggle-text">Activer le module Fidélité</span>
                </label>
                <p className="cfg-toggle-hint">
                  Affiche le menu <em>Fidélité</em> (Cartes, Récompenses, Paramètres). Points gagnés
                  automatiquement à chaque vente et récompenses notifiées par WhatsApp.
                  Si désactivé, ce menu est masqué pour tous les utilisateurs.
                </p>
              </div>
            </div>
          </div>

          {/* ── Save ── */}
          <div className="cfg-save-bar">
            <button type="submit" className="cfg-save-btn" disabled={saving}>
              {saving
                ? <><Loader size={14} className="cfg-spin"/> Enregistrement…</>
                : <><Save size={14}/> Enregistrer les modifications</>}
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}
